import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, BadgeCheck, Building2, Check, GraduationCap, MapPin,
  School, ShieldCheck, Sparkles, TrendingUp,
} from 'lucide-react';
import { PageMeta, organizationJsonLd } from '@/components/PageMeta';

/**
 * Landing (/) — newspaper front page.
 *
 * Every other marketing page has its own shape. Landing is now a
 * newspaper front page: masthead + big banner story on the left, "in
 * this issue" index on the right, then three columns (one per
 * persona) below the fold, then a "today in Nexie" band, then
 * announcements + assessments spot. Live register animation is the
 * ONE motion moment — everything else is static newsprint.
 *
 * Design spec: docs/design/marketing/_per-page-design-language.md
 * (landing gets the newspaper-front-page format).
 */

const REG_ROWS: Array<{ label: string; n: number; w: number; t: string }> = [
  { label: 'Eligible across 7 colleges', n: 1240, w: 100, t: 'auto-computed' },
  { label: 'Applied',                    n: 412,  w: 33,  t: 'posted in 1m 24s' },
  { label: 'Shortlisted',                n: 64,   w: 14,  t: 'screened in 13 min' },
  { label: 'Interviewed',                n: 40,   w: 10,  t: 'scheduled in 4 min' },
  { label: 'Offered',                    n: 21,   w: 6,   t: 'released in 2 min' },
  { label: 'Accepted',                   n: 18,   w: 5,   t: 'tracked for you' },
];

interface Column {
  key: 'students' | 'partners' | 'institutions';
  eyebrow: string;
  headline: string;
  standfirst: string;
  body: string;
  proof: { number: string; label: string };
  cite: string;
  cta: { label: string; to: string };
  icon: React.ReactNode;
}

const COLUMNS: Column[] = [
  {
    key: 'students',
    eyebrow: 'Section A · For students',
    headline: 'Nexie moves into their pockets.',
    standfirst: "Meghana Varma applied to Zoho on Friday. By Monday morning, Nexie had planned her week and rewritten her resume for the Wipro drive landing at 9:30.",
    body: 'Students on Kollez get a personal Nexie — AI Career Advisor, Resume Builder, Mock Interviewer, portable badges. Every drive they can win, waiting on their phone. Free forever. Never a fee.',
    proof: { number: '12,000+', label: 'students, partner campuses' },
    cite: '— Meghana Varma · placed at Freshworks',
    cta: { label: 'A day in the app', to: '/for-students' },
    icon: <GraduationCap size={16} />,
  },
  {
    key: 'partners',
    eyebrow: 'Section B · For partners',
    headline: 'One post reaches seven campuses.',
    standfirst: 'Wipro Elite NTH posted a Project Engineer role on Kollez. Seven partner campuses saw it in the same minute. Nexie ranked the shortlist by 4 pm — with cited reasons, human-approved.',
    body: 'Companies, training providers, certification bodies, mentors, hackathon hosts. Post any offering, filter by verified badges, hire from every partner campus. Contact hidden until shortlist.',
    proof: { number: '6w → 3d', label: 'time to hire' },
    cite: '— Priya Ramesh · Wipro Elite NTH',
    cta: { label: 'How partners hire', to: '/for-partners' },
    icon: <Building2 size={16} />,
  },
  {
    key: 'institutions',
    eyebrow: 'Section C · For institutions',
    headline: 'One number at eight AM.',
    standfirst: "Malla Reddy Group runs placement across five colleges. Dr. Ramesh opens Kollez by 8 am. Nexie has already flagged two colleges behind target — with a chase list ready.",
    body: 'Run placement, training, and reporting for one college or fifty. Group roll-up every four hours. Nexie drafts your NIRF, NAAC, AICTE. Every offer, four sign-off checks. Every acceptance, 10-minute undo.',
    proof: { number: '87 drives', label: 'up from 34 last season' },
    cite: '— Dr. Ramesh Malla · Malla Reddy Group',
    cta: { label: 'A director’s morning', to: '/for-institutions' },
    icon: <School size={16} />,
  },
];

interface IssueEntry { n: string; label: string; where: string }
const IN_THIS_ISSUE: IssueEntry[] = [
  { n: '01', label: 'Nexie in your pocket',       where: '/for-students' },
  { n: '02', label: 'One post, seven campuses',    where: '/for-partners' },
  { n: '03', label: 'A director’s morning',        where: '/for-institutions' },
  { n: '04', label: 'Anatomy of a question',       where: '/assessments' },
  { n: '05', label: 'How Nexie ranks + cites',     where: '/ai' },
  { n: '06', label: 'Talk about pricing',          where: '/pricing' },
];

/* ────────── sub-components ────────── */

function RegisterFigure({ rowsRef }: { rowsRef: React.RefObject<HTMLDivElement> }) {
  return (
    <figure
      className="card overflow-hidden shadow-md self-start w-full"
      aria-label="Example: a Wipro drive moving through the pipeline"
    >
      <div className="flex items-center gap-3 border-b border-line-tertiary px-5 py-4">
        <span className="logo-tile">W</span>
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold">Wipro Elite NTH — Project Engineer</div>
          <div className="text-sm text-ink-tertiary">7 colleges · ₹6.5 LPA · drive on 24 Sept 2026</div>
        </div>
        <span className="tier">Day 1</span>
      </div>
      <div className="relative bg-primary pb-2 pt-1" ref={rowsRef}>
        <div className="reg-margin" aria-hidden="true" />
        <div className="flex justify-between px-5 pb-1 pl-11 pt-3 text-xs text-ink-tertiary">
          <span>Stage</span>
          <span className="flex gap-10 sm:gap-[74px]">
            <span>Students</span>
            <span className="hidden sm:inline">Your time</span>
          </span>
        </div>
        {REG_ROWS.map((r) => (
          <div key={r.label} className="reg-row" data-n={r.n} data-w={r.w} data-t={r.t}>
            <Check size={12} className="reg-tick" />
            <span>{r.label}</span>
            <span className="reg-n">0</span>
            <span className="reg-t">{r.t}</span>
            <div className="reg-fill"><span /></div>
          </div>
        ))}
      </div>
      <figcaption className="px-5 py-3 text-xs text-ink-tertiary" style={{ borderTop: '1px solid rgb(var(--line-tertiary))' }}>
        Excerpt · The register a placement officer sees this morning
      </figcaption>
    </figure>
  );
}

function InThisIssue() {
  return (
    <aside
      className="border rounded-md p-5"
      style={{ borderColor: 'rgb(var(--line-primary))', background: 'rgb(var(--bg-primary))' }}
      aria-label="In this issue"
    >
      <p
        className="m-0 uppercase pb-3"
        style={{
          fontSize: '.6875rem',
          letterSpacing: '.16em',
          color: 'rgb(var(--ink-tertiary))',
          borderBottom: '1px solid rgb(var(--line-tertiary))',
        }}
      >
        In this issue
      </p>
      <ol className="mt-3 grid gap-2">
        {IN_THIS_ISSUE.map((e) => (
          <li key={e.n}>
            <Link
              to={e.where}
              className="flex items-baseline gap-3 py-1 hover:opacity-75"
            >
              <span
                className="tabular-nums font-serif"
                style={{
                  fontSize: '.9375rem',
                  color: 'rgb(var(--ink-accent))',
                  minWidth: 22,
                  fontWeight: 500,
                }}
              >
                {e.n}
              </span>
              <span
                className="flex-1"
                style={{
                  fontFamily: 'Newsreader, Georgia, serif',
                  fontSize: '1rem',
                  lineHeight: 1.35,
                }}
              >
                {e.label}
              </span>
            </Link>
          </li>
        ))}
      </ol>
    </aside>
  );
}

function ColumnStory({ col }: { col: Column }) {
  return (
    <article
      className="grid gap-3 pt-4"
      style={{ borderTop: '3px double rgb(var(--ink-primary))' }}
    >
      <div className="flex items-center gap-2">
        <span
          className="flex items-center justify-center rounded"
          style={{
            width: 22, height: 22,
            background: 'rgb(var(--bg-info))',
            color: 'rgb(var(--ink-accent))',
          }}
        >
          {col.icon}
        </span>
        <p
          className="m-0 uppercase font-medium"
          style={{
            fontSize: '.6875rem',
            letterSpacing: '.14em',
            color: 'rgb(var(--ink-tertiary))',
          }}
        >
          {col.eyebrow}
        </p>
      </div>
      <h2
        className="m-0"
        style={{
          fontFamily: 'Newsreader, Georgia, serif',
          fontSize: 'clamp(1.5rem, 2.5vw, 1.875rem)',
          lineHeight: 1.15,
          fontWeight: 500,
          letterSpacing: '-0.005em',
        }}
      >
        {col.headline}
      </h2>
      <p
        className="m-0"
        style={{
          fontFamily: 'Newsreader, Georgia, serif',
          fontSize: '1rem',
          lineHeight: 1.55,
          color: 'rgb(var(--ink-primary))',
          fontStyle: 'italic',
        }}
      >
        {col.standfirst}
      </p>
      <p className="m-0 text-sm text-ink-secondary" style={{ lineHeight: 1.6 }}>
        {col.body}
      </p>
      <div
        className="pt-3 mt-1 flex items-baseline gap-3"
        style={{ borderTop: '1px solid rgb(var(--line-tertiary))' }}
      >
        <span
          className="font-serif tabular-nums"
          style={{
            fontSize: '1.5rem',
            fontWeight: 500,
            lineHeight: 1,
            color: 'rgb(var(--ink-primary))',
            letterSpacing: '-0.005em',
          }}
        >
          {col.proof.number}
        </span>
        <span className="text-xs" style={{ color: 'rgb(var(--ink-tertiary))', maxWidth: '18ch' }}>
          {col.proof.label}
        </span>
      </div>
      <p
        className="italic m-0"
        style={{
          fontFamily: 'Newsreader, Georgia, serif',
          fontSize: '.875rem',
          color: 'rgb(var(--ink-tertiary))',
        }}
      >
        {col.cite}
      </p>
      <Link
        to={col.cta.to}
        className="link text-sm inline-flex items-center gap-1"
      >
        {col.cta.label} <ArrowRight size={14} />
      </Link>
    </article>
  );
}

/* ────────── page ────────── */

export function LandingPage() {
  const rowsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rows = rowsRef.current?.querySelectorAll<HTMLDivElement>('.reg-row') ?? [];
    rows.forEach((row, i) => {
      const n = Number(row.dataset.n ?? '0');
      const w = Number(row.dataset.w ?? '0');
      const numEl = row.querySelector<HTMLSpanElement>('.reg-n');
      const fill = row.querySelector<HTMLSpanElement>('.reg-fill span');
      const start = () => {
        row.classList.add('on');
        if (fill) fill.style.width = `${w}%`;
        if (!numEl) return;
        if (reduce) {
          numEl.textContent = n.toLocaleString('en-IN');
          return;
        }
        const t0 = performance.now();
        const step = (t: number) => {
          const p = Math.min(1, (t - t0) / 700);
          numEl.textContent = Math.round(n * (1 - Math.pow(1 - p, 3))).toLocaleString('en-IN');
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      };
      reduce ? start() : setTimeout(start, 350 + i * 420);
    });
  }, []);

  return (
    <>
      <PageMeta
        description="Kollez is the register of Indian education — where students learn, partners hire, and institutions deliver, from one platform. Nexie AI drafts, ranks, and explains. You approve every action."
        canonical="/"
        jsonLd={organizationJsonLd}
      />
      <style>{`
        .reg-row { display: grid; grid-template-columns: 1fr 88px 96px; align-items: center; gap: 14px; height: 48px; padding: 0 20px 0 44px; position: relative; }
        .reg-row + .reg-row { border-top: 1px solid rgb(var(--rule)); }
        .reg-n { font-size: 1.375rem; font-weight: 600; text-align: right; letter-spacing: -.02em; }
        .reg-t { font-size: .8125rem; color: rgb(var(--ink-tertiary)); text-align: right; white-space: nowrap; }
        .reg-fill { position: absolute; left: 44px; right: 200px; bottom: 5px; height: 2px; border-radius: 2px; background: rgb(var(--bg-tertiary)); overflow: hidden; }
        .reg-fill span { display: block; height: 100%; width: 0; background: rgb(var(--ink-accent)); transition: width .9s cubic-bezier(.2,.8,.2,1); }
        .reg-row.on .reg-t { color: rgb(var(--ink-secondary)); }
        .reg-margin { position: absolute; left: 28px; top: 0; bottom: 0; width: 1.5px; background: rgb(var(--margin)); opacity: .8; }
        .reg-tick { position: absolute; left: 10px; width: 12px; height: 12px; color: rgb(var(--ink-success)); opacity: 0; transition: opacity .3s; }
        .reg-row.on .reg-tick { opacity: 1; }
        @media (max-width: 639px) {
          .reg-row { grid-template-columns: minmax(0,1fr) 64px; padding-right: 14px; }
          .reg-t { display: none; }
          .reg-fill { right: 100px; }
        }
      `}</style>

      {/* BANNER STORY + IN-THIS-ISSUE */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 pt-16 lg:pt-24 pb-8 grid gap-8 lg:grid-cols-[1fr_260px] lg:gap-12">
        <div className="grid gap-6">
          <p
            className="m-0 uppercase font-medium flex items-center gap-2"
            style={{
              fontSize: '.75rem',
              letterSpacing: '.14em',
              color: 'rgb(var(--ink-accent))',
            }}
          >
            <Sparkles size={12} />
            Front-page story · this season
          </p>
          <h1
            className="display m-0 text-ink-primary"
            style={{ textWrap: 'balance' as never }}
          >
            Students learn. Partners hire.<br />
            Institutions deliver.<br />
            <span style={{ color: 'rgb(var(--ink-accent))', fontStyle: 'italic' }}>
              From one register.
            </span>
          </h1>
          <p className="m-0 text-lg leading-8 text-ink-secondary" style={{ maxWidth: '58ch' }}>
            One place where AI helps students grow into emerging skills,
            partners find and grow talent, and institutions deliver more
            to every student — powered by{' '}
            <Link to="/ai" className="link">Nexie</Link>, our
            education-tuned AI.
          </p>
          <div className="flex flex-wrap gap-3 mt-2">
            <Link to="/contact?topic=demo" className="btn btn-primary btn-lg">
              Book a 30-min demo
            </Link>
            <Link to="/ai" className="btn btn-outline btn-lg">
              Meet Nexie
            </Link>
          </div>
          <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink-secondary mt-2" aria-label="Trust signals">
            <li className="flex items-center gap-1.5"><Sparkles size={14} className="text-ink-accent" />Nexie cites every source</li>
            <li className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-ink-success" />DPDP Act 2023 ready</li>
            <li className="flex items-center gap-1.5"><MapPin size={14} className="text-ink-success" />Data stays in India</li>
          </ul>
        </div>

        <InThisIssue />
      </section>

      {/* REGISTER FIGURE — the identity motion moment */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 pb-16">
        <RegisterFigure rowsRef={rowsRef} />
      </section>

      {/* PRODUCT FLOW — the two-way exchange diagram */}
      <section
        className="border-t border-line-tertiary py-16 lg:py-20"
        aria-labelledby="product-flow-heading"
      >
        <div className="mx-auto max-w-page-wide px-5 lg:px-10">
          <div className="max-w-3xl mb-10">
            <p
              className="m-0 uppercase font-medium mb-3"
              style={{ fontSize: '.75rem', letterSpacing: '.14em', color: 'rgb(var(--ink-tertiary))' }}
            >
              How the exchange works
            </p>
            <h2 id="product-flow-heading" className="h-display-2 m-0" style={{ color: 'rgb(var(--ink-primary))' }}>
              Every lane carries traffic both ways.
            </h2>
            <p className="mt-3 text-ink-secondary max-w-2xl">
              A student joins once. Hiring partners find candidates already tested. Training partners get
              cohorts picked by evidence. Nexie sits in every student's pocket as a career advisor.
            </p>
          </div>
          <figure className="m-0 kollez-flow">
            {/* Light theme image (rendered under [data-theme="ruled"]) */}
            <img
              src="/brand/kollez-product-flow.svg"
              alt="The kollez.ai exchange: institutions run kollez.ai; kollez.ai matches every student to opportunities with assessments; hiring partners get tested candidates on the left, training partners get evidence-picked cohorts on the right."
              loading="lazy"
              className="kollez-flow-light w-full h-auto"
            />
            {/* Dark theme image (rendered under [data-theme="blackboard"]) */}
            <img
              src="/brand/kollez-product-flow-dark.svg"
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="kollez-flow-dark w-full h-auto"
            />
          </figure>
        </div>
        <style>{`
          .kollez-flow-dark { display: none; }
          [data-theme="blackboard"] .kollez-flow-light { display: none; }
          [data-theme="blackboard"] .kollez-flow-dark { display: block; }
          @media (prefers-color-scheme: dark) {
            :root:not([data-theme="ruled"]) .kollez-flow-light { display: none; }
            :root:not([data-theme="ruled"]) .kollez-flow-dark { display: block; }
          }
        `}</style>
      </section>

      {/* THREE COLUMNS — one per persona, below the fold */}
      <section
        className="border-t border-line-tertiary py-16 lg:py-20"
        style={{ background: 'rgb(var(--bg-secondary))' }}
      >
        <div className="mx-auto max-w-page-wide px-5 lg:px-10 grid gap-10 lg:grid-cols-3 lg:gap-8">
          {COLUMNS.map((c) => <ColumnStory key={c.key} col={c} />)}
        </div>
      </section>

      {/* NEXIE BAND — full-width inkwell strip like a special section */}
      <section
        className="inkwell py-16 lg:py-20"
      >
        <div className="mx-auto max-w-page-wide px-5 lg:px-10 grid gap-8 lg:grid-cols-[1fr_1.4fr] lg:gap-16 items-center">
          <div>
            <p
              className="m-0 uppercase font-medium"
              style={{
                fontSize: '.75rem',
                letterSpacing: '.14em',
                color: 'rgb(232,212,122)',
              }}
            >
              Today in Nexie
            </p>
            <h2
              className="m-0 mt-3"
              style={{
                fontFamily: 'Newsreader, Georgia, serif',
                fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
                lineHeight: 1.1,
                fontWeight: 500,
                letterSpacing: '-0.005em',
                color: '#fff',
              }}
            >
              The AI that reads the register you keep.
            </h2>
            <p
              className="m-0 mt-4"
              style={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.6, maxWidth: '46ch' }}
            >
              Nexie is Kollez&apos;s education-tuned AI. It drafts, ranks and
              explains — grounded in your workspace, cites every source,
              and never takes an autonomous action.
            </p>
            <Link
              to="/ai"
              className="inline-flex items-center gap-1 mt-6 font-medium"
              style={{ color: 'rgb(232,212,122)' }}
            >
              Read the Nexie section <ArrowRight size={14} />
            </Link>
          </div>
          <ol className="grid gap-4" style={{ counterReset: 'nexie' }}>
            {[
              { t: 'Grounded, not generated', b: 'Every answer cites the row it drew from. If Nexie can\'t find a source, it says "I don\'t know" instead of making something up.' },
              { t: 'Human sign-off always',   b: 'Every publish, offer, shortlist, report needs a person on your team to approve. Nexie proposes; you dispose.' },
              { t: 'No cross-tenant training', b: 'Your students\' data does not train a model shared with any other college or partner. Ever.' },
            ].map((p, i) => (
              <li key={p.t} className="grid grid-cols-[36px_1fr] gap-4 items-baseline">
                <span
                  className="tabular-nums font-serif"
                  style={{
                    fontSize: '1.375rem',
                    fontWeight: 500,
                    color: 'rgb(232,212,122)',
                    lineHeight: 1,
                  }}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3
                    className="m-0"
                    style={{
                      fontFamily: 'Newsreader, Georgia, serif',
                      fontSize: '1.125rem',
                      fontWeight: 500,
                      color: '#fff',
                      letterSpacing: '-0.005em',
                    }}
                  >
                    {p.t}
                  </h3>
                  <p className="m-0 mt-1" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '.9375rem', lineHeight: 1.55 }}>
                    {p.b}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ANNOUNCEMENTS + ASSESSMENTS + PROOF STRIP */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 py-16 lg:py-20 grid gap-8">
        <div className="flex items-baseline justify-between flex-wrap gap-2 pb-3" style={{ borderBottom: '2px solid rgb(var(--ink-primary))' }}>
          <p
            className="m-0 uppercase"
            style={{
              fontFamily: 'Newsreader, Georgia, serif',
              fontSize: '1.125rem',
              fontStyle: 'italic',
              letterSpacing: '.02em',
              color: 'rgb(var(--ink-primary))',
            }}
          >
            Announcements &amp; late-breaking
          </p>
          <p
            className="m-0 uppercase tabular-nums"
            style={{
              fontSize: '.6875rem',
              letterSpacing: '.14em',
              color: 'rgb(var(--ink-tertiary))',
            }}
          >
            Page 4 · Continued
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              eyebrow: 'New this season',
              headline: 'Six assessment modes. Twelve question types.',
              body: 'Fixed sets, adaptive draws, Nexie-chat, live coding, take-home, voice — one anatomy per question, six deliveries.',
              cta: { label: 'Anatomy of a question →', to: '/assessments' },
            },
            {
              eyebrow: 'Ethics reprint',
              headline: 'Students never pay. Ever.',
              body: 'If anyone asks you for a fee to apply, interview, or accept through Kollez — it\'s a scam. We act within a day.',
              cta: { label: 'Report a fraud attempt →', to: '/contact?topic=fraud' },
            },
            {
              eyebrow: 'From the compliance desk',
              headline: 'SOC 2, ISO 27001, DPDP Act 2023.',
              body: 'The plain-English walkthrough of how Kollez treats student PII, offers, and audit obligations.',
              cta: { label: 'Read the security doc →', to: '/security' },
            },
          ].map((a) => (
            <article
              key={a.headline}
              className="grid gap-2 pt-4"
              style={{ borderTop: '1px solid rgb(var(--line-tertiary))' }}
            >
              <p
                className="m-0 uppercase font-medium"
                style={{
                  fontSize: '.6875rem',
                  letterSpacing: '.12em',
                  color: 'rgb(var(--ink-accent))',
                }}
              >
                {a.eyebrow}
              </p>
              <h3
                className="m-0"
                style={{
                  fontFamily: 'Newsreader, Georgia, serif',
                  fontSize: '1.25rem',
                  fontWeight: 500,
                  lineHeight: 1.2,
                  letterSpacing: '-0.005em',
                }}
              >
                {a.headline}
              </h3>
              <p className="m-0 text-sm text-ink-secondary" style={{ lineHeight: 1.55 }}>
                {a.body}
              </p>
              <Link to={a.cta.to} className="link text-sm inline-flex items-center gap-1 mt-1">
                {a.cta.label.replace(' →', '')} <ArrowRight size={14} />
              </Link>
            </article>
          ))}
        </div>
      </section>

      {/* CIRCULATION STRIP — customers logos as "circulated at" */}
      <section
        className="border-y border-line-tertiary py-12 lg:py-14"
        style={{ background: 'rgb(var(--bg-primary))' }}
      >
        <div className="mx-auto max-w-page-wide px-5 lg:px-10">
          <p
            className="text-center uppercase mb-6"
            style={{
              fontSize: '.6875rem',
              letterSpacing: '.16em',
              color: 'rgb(var(--ink-tertiary))',
            }}
          >
            Circulated in placement cells across
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
            {[
              'Malla Reddy Group',
              'Godavari Institute',
              'Sri Vasavi',
              'Chaitanya Bharathi',
              'ICFAI',
              'JNTU-K',
              'K L University',
              'GITAM',
            ].map((name) => (
              <span
                key={name}
                className="italic"
                style={{
                  fontFamily: 'Newsreader, Georgia, serif',
                  fontSize: '1rem',
                  color: 'rgb(var(--ink-secondary))',
                }}
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL FRONT-PAGE CTA */}
      <section className="mx-auto max-w-page-wide px-5 py-16 lg:px-10 lg:py-20">
        <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr] items-end pb-6" style={{ borderBottom: '3px double rgb(var(--ink-primary))' }}>
          <div>
            <p
              className="m-0 uppercase font-medium"
              style={{
                fontSize: '.75rem',
                letterSpacing: '.14em',
                color: 'rgb(var(--ink-accent))',
              }}
            >
              Subscribe to the next season
            </p>
            <h2
              className="m-0 mt-2 text-ink-primary"
              style={{
                fontFamily: 'Newsreader, Georgia, serif',
                fontSize: 'clamp(1.75rem, 4vw, 2.75rem)',
                lineHeight: 1.05,
                fontWeight: 500,
                letterSpacing: '-0.01em',
              }}
            >
              Start with one college this placement season.
            </h2>
            <p className="text-ink-secondary mt-3 m-0" style={{ maxWidth: '58ch' }}>
              Free pilot for one college, one season. No card, no PO, no
              lock-in. Upgrade when it earns the upgrade.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Link to="/contact?topic=demo" className="btn btn-primary btn-lg">
              Book a 30-min demo
            </Link>
            <Link to="/register/institution?plan=pilot" className="btn btn-outline btn-lg">
              Start a free pilot
            </Link>
          </div>
        </div>
        <div className="mt-6 grid gap-2 md:grid-cols-2 lg:grid-cols-4 text-xs" style={{ color: 'rgb(var(--ink-tertiary))' }}>
          <span className="flex items-center gap-1.5"><TrendingUp size={12} />Placement season 2026–27 open</span>
          <span className="flex items-center gap-1.5"><BadgeCheck size={12} />Verified employer program live</span>
          <span className="flex items-center gap-1.5"><Sparkles size={12} />Nexie AI — cited and audited</span>
          <span className="flex items-center gap-1.5"><MapPin size={12} />Hyderabad, made in India</span>
        </div>
      </section>
    </>
  );
}
